import React, { useState } from 'react';
import { useUpdateClassMutation } from '../../store/api/localApi';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface EditClassModalProps {
  cls: any;
  onClose: () => void;
}

const EditClassModal: React.FC<EditClassModalProps> = ({ cls, onClose }) => {
  const [updateClass, { isLoading }] = useUpdateClassMutation();
  const [form, setForm] = useState({
    name: cls.name,
    room: cls.room || '',
    description: cls.description || '',
    startTime: cls.startTime,
    endTime: cls.endTime,
    capacity: cls.capacity?.toString() || '',
    daysOfWeek: cls.daysOfWeek || [],
  });
  const [error, setError] = useState('');

  const toggleDay = (day: number) => {
    setForm((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d: number) => d !== day)
        : [...prev.daysOfWeek, day].sort(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await updateClass({
        classId: cls.id,
        updates: {
          name: form.name,
          // Sent even when empty: an empty room clears it server-side.
          room: form.room,
          description: form.description || undefined,
          startTime: form.startTime,
          endTime: form.endTime,
          daysOfWeek: form.daysOfWeek,
          capacity: form.capacity ? parseInt(form.capacity) : undefined,
        },
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to update class');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="edit-class-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Class</h3>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Room</label>
              <input
                data-testid="edit-class-room"
                value={form.room}
                onChange={(e) => setForm((p) => ({ ...p, room: e.target.value }))}
                maxLength={60}
                placeholder="e.g. Blue Room"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Time</label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
              <div className="flex gap-2">
                {DAY_NAMES.map((name, idx) => {
                  const selected = form.daysOfWeek.includes(idx);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      data-testid={`edit-class-day-${idx}`}
                      aria-pressed={selected}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                        selected
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="edit-class-submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditClassModal;
